import { create } from "zustand";
import type { TChapterStatusMap } from "@/zapi-frontend/shared/hooks/useProjectSetupPolling";

type TGenerationStatus =
  | "pending"
  | "processing"
  | "streaming"
  | "ready"
  | "processed"
  | "error"
  | "errorSeen"
  | "cancelled";

interface ProjectSetupStore {
  activeSetupTasks: Record<string, number>;
  taskStatuses: Record<string, TGenerationStatus>;
  pollingProjects: Record<string, boolean>;
  chapterStatusMaps: Record<string, TChapterStatusMap | null>;
  setSetupTask: (projectId: string, taskId: number) => void;
  updateTaskStatus: (projectId: string, status: TGenerationStatus) => void;
  setChapterStatusMap: (projectId: string, map: TChapterStatusMap | null) => void;
  clearSetupTask: (projectId: string) => void;
  getSetupTask: (projectId: string) => number | null;
  setPolling: (projectId: string, isPolling: boolean) => void;
}

export const useProjectSetupStore = create<ProjectSetupStore>()((set, get) => ({
  activeSetupTasks: {},
  taskStatuses: {},
  pollingProjects: {},
  chapterStatusMaps: {},

  setSetupTask: (projectId, taskId) =>
    set((state) => ({
      activeSetupTasks: { ...state.activeSetupTasks, [projectId]: taskId }
    })),

  updateTaskStatus: (projectId, status) =>
    set((state) => ({
      taskStatuses: { ...state.taskStatuses, [projectId]: status }
    })),

  setChapterStatusMap: (projectId, map) =>
    set((state) => ({
      chapterStatusMaps: { ...state.chapterStatusMaps, [projectId]: map }
    })),

  clearSetupTask: (projectId) =>
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _task, ...restTasks } = state.activeSetupTasks;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _status, ...restStatuses } = state.taskStatuses;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [projectId]: _map, ...restMaps } = state.chapterStatusMaps;
      return { activeSetupTasks: restTasks, taskStatuses: restStatuses, chapterStatusMaps: restMaps };
    }),

  getSetupTask: (projectId) => get().activeSetupTasks[projectId] || null,

  setPolling: (projectId, isPolling) =>
    set((state) => ({
      pollingProjects: { ...state.pollingProjects, [projectId]: isPolling }
    }))
}));
